import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import Paper from '@mui/material/Paper';
import { styled } from '@mui/material/styles';

import * as Styled from './RxGateReceiptTable.styles';
import { rxGateDistributeClientBonus } from '../../helpers';
import { RxGateCurrentReceiptItem } from '../../types';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    fontSize: 14,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 16,
  },
}));

const RxGateReceiptTable = ({
  currentReceipt,
}: {
  currentReceipt: RxGateCurrentReceiptItem[];
}) => {
  const { t } = useTranslation();

  const rows = useMemo(
    () => rxGateDistributeClientBonus(currentReceipt, 0),
    [currentReceipt]
  );

  const customErrTooltip = (err: string) => {
    if (!err) return null;

    const productErrText = () => {
      switch (err) {
        case 'mapping_not_found': // нет привязки
        case 'magento_product_not_found': // карточки товара нет в системе
        case 'alo_product_not_found': // нет соответствия medicipeAloProduct
        case 'invalid_quantity': // неверное количество товара
          return t(`RXGatePage.Receipt_errors.${err.toUpperCase()}`);

        default:
          return err;
      }
    };

    return (
      <Tooltip title={productErrText()} placement="right-start">
        <img
          src="/images/client_not_added.png"
          width={20}
          height={20}
          alt="icon"
        />
      </Tooltip>
    );
  };

  return (
    <Styled.ReceiptTableContainer>
      <TableContainer component={Paper}>
        <Table
          sx={{ minWidth: 550, tableLayout: 'fixed' }}
          aria-label="simple table"
        >
          <TableHead>
            <TableRow>
              <StyledTableCell width="5%" align="center">
                №
              </StyledTableCell>
              <StyledTableCell width="45%" align="center">
                {t('LoyaltyPage.Table.PRODUCT_NAME')}
              </StyledTableCell>
              <StyledTableCell width="10%" align="center">
                {t('LoyaltyPage.Table.PRODUCT_COUNT')}
              </StyledTableCell>
              <StyledTableCell width="10%" align="center">
                {t('LoyaltyPage.Table.PRODUCT_PRICE')}
              </StyledTableCell>
              <StyledTableCell width="10%" align="center">
                CostCo
              </StyledTableCell>
              <StyledTableCell width="15%" align="center">
                {t('LoyaltyPage.Table.SUM')}
              </StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => (
              <TableRow key={row.sku}>
                <StyledTableCell width="10px" align="center" padding="none">
                  {row.product_err
                    ? customErrTooltip(row.product_err.code)
                    : index + 1}
                </StyledTableCell>
                <StyledTableCell width="60px" align="center" padding="none">
                  {row.name}
                </StyledTableCell>
                <StyledTableCell width="60px" align="center" padding="none">
                  {row.quantity}
                </StyledTableCell>
                <StyledTableCell width="60px" align="center" padding="none">
                  {row.price.toFixed(2)} ₸
                </StyledTableCell>
                <StyledTableCell width="60px" align="center" padding="none">
                  {row.cost_co ? `${row.cost_co} ₸` : '-'}
                </StyledTableCell>
                <StyledTableCell width="60px" align="center" padding="none">
                  {row.appliedBonus > 0 && (
                    <span
                      style={
                        row.appliedBonus > 0
                          ? {
                              textDecoration: 'line-through',
                              marginRight: '5px',
                            }
                          : {}
                      }
                    >
                      {row.price * row.quantity}
                    </span>
                  )}
                  {row.price * row.quantity > 0 && (
                    <span>
                      {Math.round(row.price * row.quantity - row.appliedBonus)}{' '}
                      ₸
                    </span>
                  )}
                </StyledTableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Styled.ReceiptTableContainer>
  );
};

export default RxGateReceiptTable;
